import { logPageActionToNewRelic } from "../newRelic-log-utils";
import { MONITORING_SAMPLE_EVENT } from "./constants";
import { getThresholdSummary, normalizeAttributes } from "./helpers";
import type {
  MonitoringMetricDefinition,
  MonitoringMetricSample,
  PodMonitoringSchema,
} from "./types";

// Sends a normalized monitoring sample to New Relic with shared schema metadata.
function emitMonitoringMetricSample(
  schema: PodMonitoringSchema,
  metric: MonitoringMetricDefinition,
  sample: MonitoringMetricSample
): void {
  logPageActionToNewRelic(MONITORING_SAMPLE_EVENT, {
    pod: schema.pod,
    schema_id: schema.schemaId,
    metric_id: metric.id,
    metric_name: metric.name,
    metric_category: metric.category,
    metric_source: metric.source,
    metric_status: metric.status,
    metric_unit: sample.unit ?? metric.unit,
    metric_value: sample.value,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    page_url: typeof window !== "undefined" ? window.location.href : "",
    status_code: sample.statusCode ?? null,
    success: sample.success ?? null,
    sample_percentile: sample.percentile ?? null,
    ...getThresholdSummary(metric),
    ...normalizeAttributes(metric.meta),
    ...normalizeAttributes(sample.extra),
  });
}

// Records a sample for a known metric id and reports whether the metric existed.
export function recordMonitoringMetricSample(
  schema: PodMonitoringSchema,
  metricId: string,
  sample: MonitoringMetricSample
): boolean {
  const metric = schema.metrics.find((candidate) => candidate.id === metricId);

  if (!metric) {
    return false;
  }

  emitMonitoringMetricSample(schema, metric, sample);
  return true;
}

// Emits API metric samples while silently ignoring unknown metric ids.
export function emitApiMetricSample(
  schema: PodMonitoringSchema,
  metricId: string,
  sample: MonitoringMetricSample
): void {
  const metric = schema.metrics.find((candidate) => candidate.id === metricId);

  if (!metric) {
    return;
  }

  emitMonitoringMetricSample(schema, metric, sample);
}
